/**
 * Apps Logo Slider for @ollaid/native-sso
 * Matches Web SSO badge style: horizontal marquee, small icons, no app names
 */

import { useEffect, useState } from 'react';
import { getNativeAuthConfig } from '../services/api';

interface AppsLogoSliderProps {
  speed?: 'slow' | 'normal' | 'fast';
  className?: string;
  iamApiUrl?: string;
}

interface AppInfo { id: number; name: string; logo: string | null; }

export function AppsLogoSlider({ className = '', iamApiUrl: iamApiUrlProp }: AppsLogoSliderProps) {
  const [applications, setApplications] = useState<AppInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const resolvedIamApiUrl = iamApiUrlProp || getNativeAuthConfig().iamApiUrl;
    if (!resolvedIamApiUrl) return;
    const fetchApps = async () => {
      try {
        const iamBaseUrl = resolvedIamApiUrl.replace('/api', '');
        const response = await fetch(`${iamBaseUrl}/api/public/applications`, { headers: { 'Accept': 'application/json' } });
        if (response.ok) {
          const data = await response.json();
          if (data.success && Array.isArray(data.data)) setApplications(data.data);
        }
      } catch { /* ignore */ } finally { setIsLoading(false); }
    };
    fetchApps();
  }, [iamApiUrlProp]);

  const getLogoUrl = (logo: string | null): string | null => {
    if (!logo) return null;
    if (logo.startsWith('http')) return logo;
    const resolvedUrl = iamApiUrlProp || getNativeAuthConfig().iamApiUrl;
    return `${resolvedUrl.replace('/api', '')}/storage/applications/${logo}`;
  };

  if (isLoading) {
    return (
      <div className={className} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ display: 'flex', gap: '0.75rem', padding: '0 0.75rem' }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} style={{ width: '2rem', height: '2rem', borderRadius: '0.5rem', backgroundColor: '#e5e7eb', flexShrink: 0 }} />
          ))}
        </div>
      </div>
    );
  }

  if (applications.length === 0) return null;

  // Triple for smooth infinite loop (same as web SSO)
  const displayApps = [...applications, ...applications, ...applications];

  return (
    <div
      className={className}
      style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}
    >
      {/* Left gradient mask */}
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: '1.5rem',
        background: 'linear-gradient(to right, #ffffff, transparent)',
        zIndex: 10, pointerEvents: 'none',
      }} />

      {/* Marquee content */}
      <div style={{
        display: 'flex',
        animation: 'ollaid-marquee 20s linear infinite',
        height: '100%',
        alignItems: 'center',
      }}>
        {displayApps.map((app, index) => {
          const logoUrl = getLogoUrl(app.logo);
          return (
            <div
              key={`${app.id}-${index}`}
              style={{ flexShrink: 0, padding: '0 0.75rem', display: 'flex', alignItems: 'center', height: '100%' }}
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={app.name}
                  style={{ width: '2rem', height: '2rem', objectFit: 'contain' }}
                  onError={(e) => {
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    if (target.nextElementSibling) {
                      (target.nextElementSibling as HTMLElement).style.display = 'flex';
                    }
                  }}
                />
              ) : null}
              <div
                style={{
                  width: '2rem',
                  height: '2rem',
                  borderRadius: '0.5rem',
                  backgroundColor: '#f3f4f6',
                  alignItems: 'center',
                  justifyContent: 'center',
                  display: logoUrl ? 'none' : 'flex',
                }}
              >
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6b7280' }}>
                  {app.name?.charAt(0)?.toUpperCase() || 'A'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Right gradient mask */}
      <div style={{
        position: 'absolute', right: 0, top: 0, bottom: 0, width: '1.5rem',
        background: 'linear-gradient(to left, #ffffff, transparent)',
        zIndex: 10, pointerEvents: 'none',
      }} />
    </div>
  );
}

export default AppsLogoSlider;
